import type { SandboxSystemsInput } from '../transpiler';
import { normalizeProjectPath } from './paths';
import { emitScaffoldFiles } from './scaffold';
import {
  DEFAULT_TOPOLOGY_PATHS,
  allocateTopologyPaths,
  emitSystemRegistry,
  emitTopologyFiles,
  systemFilePaths,
  type TopologyPaths,
} from './topology';
import { emptyFoldersOf, fromV1View } from './v1View';

/** A v1 project as a real project tree, with where its topology went and every path the migration generated. */
export interface MigratedProject {
  files: Record<string, string>;
  topology: TopologyPaths;
  /** Every path a v1 sandbox never had — the root files, the topology, the registries: what the v1 view leaves out. */
  generated: ReadonlySet<string>;
  /** The folders the v1 project listed with no file of their own (`src/client/components`): a tree of files cannot hold them. */
  folders: string[];
}

/**
 * The two registries, from the tree as it is now — what the site rewrites when a system file is added, renamed or
 * removed, or changes what it exports.
 */
export function refreshSystemRegistries(files: Record<string, string>, topology: TopologyPaths): Record<string, string> {
  const byPath = new Map(Object.entries(files).map(([path, code]) => [normalizeProjectPath(path), code]));
  const sources = (side: 'client' | 'server') => systemFilePaths(files, side).map((path) => ({ path, code: byPath.get(path)! }));
  return {
    [topology.clientRegistry]: emitSystemRegistry('client', topology.clientRegistry, sources('client')),
    [topology.serverRegistry]: emitSystemRegistry('server', topology.serverRegistry, sources('server')),
  };
}

/**
 * A v1 sandbox project as a real Gotti project: the person's files moved under `src/` verbatim and in load order,
 * the topology the sandbox runtime synthesizes written out as Game and Area files, the system registries, the web
 * client entry and the root files — every generated file at a path none of the person's files use. Pure and
 * deterministic: the same project always migrates to the same files.
 */
export function migrateV1Project(systems: SandboxSystemsInput, options: { name: string }): MigratedProject {
  const userFiles = fromV1View(systems);
  const topology = allocateTopologyPaths(new Set(Object.keys(userFiles)));
  const scaffold = emitScaffoldFiles({ name: options.name, paths: topology });
  const topologyFiles = emitTopologyFiles(topology);
  const withTopology = { ...userFiles, ...topologyFiles };
  const registries = refreshSystemRegistries(withTopology, topology);
  // Root files first, then the person's files in load order, then the generated src/ files.
  const files = { ...scaffold, ...userFiles, ...topologyFiles, ...registries };
  const generated = new Set([...Object.keys(scaffold), ...Object.keys(topologyFiles), ...Object.keys(registries)]);
  return { files, topology, generated, folders: emptyFoldersOf(systems) };
}

/**
 * A project as a `.gbox` document's `project` section stores it (`GboxProject` in `@gotti/sandbox-shared`): the tree,
 * plus what a tree cannot say about itself — where its topology is, which paths were generated (what the v1 view leaves
 * out), and its empty folders.
 */
export interface SavedProject {
  files: Record<string, string>;
  topology?: TopologyPaths;
  generated?: string[];
  folders?: string[];
  /** The section's format (`PROJECT_SECTION_FORMAT`); a section written before formats were recorded has none. */
  format?: number;
}

/**
 * The `project` section format this site writes and reads. A site that adds to the section what an older one cannot
 * keep raises it, so the older site refuses the section instead of opening part of it and saving the rest away.
 */
export const PROJECT_SECTION_FORMAT = 1;

/** `project` as a `.gbox` document's `project` section stores it. */
export function toSavedProject(project: MigratedProject): Required<SavedProject> {
  return {
    files: { ...project.files },
    topology: { ...project.topology },
    generated: [...project.generated],
    folders: [...project.folders],
    format: PROJECT_SECTION_FORMAT,
  };
}

/**
 * A saved project, read back. Refuses one a newer site wrote in a format this one does not know (opening part of it
 * would save the rest away), and one that lost its topology — or any of its paths — or its generated set, naming what
 * is missing: without them the v1 view would take the generated files for the person's own.
 */
export function fromSavedProject(saved: SavedProject): MigratedProject {
  if (typeof saved.format === 'number' && saved.format > PROJECT_SECTION_FORMAT) {
    throw new Error('This sandbox was saved by a newer version of the site, and this one cannot keep all of it — reload the page to open it.');
  }
  const topology = saved.topology as Partial<Record<keyof TopologyPaths, unknown>> | undefined;
  const missing = [
    ...(topology
      ? (Object.keys(DEFAULT_TOPOLOGY_PATHS) as Array<keyof TopologyPaths>).filter((key) => typeof topology[key] !== 'string').map((key) => `topology.${key}`)
      : ['topology']),
    ...(Array.isArray(saved.generated) ? [] : ['generated']),
  ];
  if (missing.length) {
    throw new Error(`A saved project needs its \`topology\` and \`generated\` to be read back: missing ${missing.join(', ')}.`);
  }
  const folders = saved.folders ?? [];
  if (!Array.isArray(folders) || folders.some((folder) => typeof folder !== 'string')) {
    throw new Error('A saved project’s `folders` must be a list of folder paths.');
  }
  return { files: { ...saved.files }, topology: { ...saved.topology! }, generated: new Set(saved.generated), folders: [...folders] };
}
